import { autoUpdate, computePosition, flip, offset, shift, type VirtualElement } from '@floating-ui/dom'
import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react'
import { createPortal } from 'react-dom'
import { setBlockType, toggleMark } from 'prosemirror-commands'
import { wrapInList } from 'prosemirror-schema-list'
import { EditorState, type Command } from 'prosemirror-state'
import { EditorView } from 'prosemirror-view'
import { createMarkdownPlugins, markdownSchema, parseMarkdown, serializeMarkdown } from './prosemirrorMarkdown'

export interface MarkdownEditorHandle {
  focus: () => void
  getMarkdown: () => string
}

type MarkdownEditorProps = {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  className?: string
  ariaLabel?: string
  placeholder?: string
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void
  toolbarMode?: 'selection' | 'always' | 'none'
  compactTrailingParagraph?: boolean
}

type SelectionRect = { left: number; right: number; top: number; bottom: number }
type FloatingStyle = { left: number; top: number }
type ActiveMarks = { strong: boolean; em: boolean }

export const MarkdownEditor = forwardRef<MarkdownEditorHandle, MarkdownEditorProps>(function MarkdownEditor({ value, onChange, disabled = false, className = '', ariaLabel = 'Markdown 内容', placeholder = '开始输入…', onKeyDown, toolbarMode = 'selection', compactTrailingParagraph = false }, ref) {
  const containerRef = useRef<HTMLDivElement>(null)
  const editorRef = useRef<HTMLDivElement>(null)
  const toolbarRef = useRef<HTMLDivElement>(null)
  const viewRef = useRef<EditorView | undefined>(undefined)
  const onChangeRef = useRef(onChange)
  const disabledRef = useRef(disabled)
  const [formatOpen, setFormatOpen] = useState(false)
  const [listOpen, setListOpen] = useState(false)
  const [selectionRect, setSelectionRect] = useState<SelectionRect>()
  const [toolbarStyle, setToolbarStyle] = useState<FloatingStyle>()
  const [activeMarks, setActiveMarks] = useState<ActiveMarks>({ strong: false, em: false })

  onChangeRef.current = onChange
  disabledRef.current = disabled

  const syncActiveMarks = () => {
    const view = viewRef.current
    if (!view) return
    const { selection } = view.state
    const marks = view.state.storedMarks || selection.$from.marks()
    if (selection.empty) {
      setActiveMarks({
        strong: Boolean(markdownSchema.marks.strong.isInSet(marks)),
        em: Boolean(markdownSchema.marks.em.isInSet(marks)),
      })
      return
    }
    setActiveMarks({
      strong: view.state.doc.rangeHasMark(selection.from, selection.to, markdownSchema.marks.strong),
      em: view.state.doc.rangeHasMark(selection.from, selection.to, markdownSchema.marks.em),
    })
  }

  const updateEmptyState = () => {
    const view = viewRef.current
    if (!view) return
    const first = view.state.doc.firstChild
    const isEmptyParagraph = view.state.doc.childCount === 1 && first?.type === markdownSchema.nodes.paragraph && !first.textContent.trim()
    view.dom.classList.toggle('is-empty', isEmptyParagraph)
  }

  const clearSelectionToolbar = () => {
    setSelectionRect(undefined)
    setToolbarStyle(undefined)
    setFormatOpen(false)
    setListOpen(false)
  }

  useImperativeHandle(ref, () => ({
    focus: () => viewRef.current?.focus(),
    getMarkdown: () => viewRef.current ? serializeMarkdown(viewRef.current.state.doc) : value,
  }), [value])

  useLayoutEffect(() => {
    const mount = editorRef.current
    if (!mount) return

    let view: EditorView | undefined
    const state = EditorState.create({ doc: parseMarkdown(value), plugins: createMarkdownPlugins({ disableGapCursor: compactTrailingParagraph }) })
    const createdView = new EditorView({ mount }, {
      state,
      editable: () => !disabledRef.current,
      dispatchTransaction: transaction => {
        if (!view) return
        let nextState = view.state.apply(transaction)
        const last = nextState.doc.lastChild
        const previous = nextState.doc.childCount > 1 ? nextState.doc.child(nextState.doc.childCount - 2) : undefined
        const isTrailingParagraphAfterList = compactTrailingParagraph && last?.type === markdownSchema.nodes.paragraph && !last.textContent.trim() && Boolean(previous && (previous.type === markdownSchema.nodes.ordered_list || previous.type === markdownSchema.nodes.bullet_list))
        if (isTrailingParagraphAfterList && last) {
          const from = nextState.doc.content.size - last.nodeSize
          nextState = nextState.apply(nextState.tr.delete(from, nextState.doc.content.size))
        }
        view.updateState(nextState)
        updateEmptyState()
        syncActiveMarks()
        onChangeRef.current(serializeMarkdown(nextState.doc))
      },
    })
    view = createdView
    viewRef.current = createdView
    updateEmptyState()
    syncActiveMarks()

    return () => {
      createdView.destroy()
      viewRef.current = undefined
    }
  }, [])

  useEffect(() => {
    const view = viewRef.current
    if (!view) return
    view.setProps({ editable: () => !disabled })
  }, [disabled])

  useEffect(() => {
    const view = viewRef.current
    if (!view) return
    const current = serializeMarkdown(view.state.doc)
    if (current === value) return
    view.updateState(EditorState.create({ doc: parseMarkdown(value), plugins: createMarkdownPlugins({ disableGapCursor: compactTrailingParagraph }) }))
    updateEmptyState()
    syncActiveMarks()
  }, [value])

  useEffect(() => {
    const handleDocumentClick = (event: globalThis.MouseEvent) => {
      const elementTarget = event.target as Element | null
      if (elementTarget?.closest?.('.markdown-editor-toolbar')) return
      if (elementTarget?.closest?.('.markdown-editor-surface')) {
        setFormatOpen(false)
        setListOpen(false)
        return
      }
      clearSelectionToolbar()
    }
    let frame = 0
    const updateSelectionToolbar = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const view = viewRef.current
        if (toolbarMode !== 'selection') return
        const toolbarHasFocus = Boolean(toolbarRef.current?.contains(document.activeElement))
        const nativeSelection = document.getSelection()
        const selectionAnchor = nativeSelection?.anchorNode
        const selectionInEditor = Boolean(selectionAnchor && editorRef.current?.contains(selectionAnchor))
        if (!view || (!selectionInEditor && !toolbarHasFocus)) {
          clearSelectionToolbar()
          return
        }
        syncActiveMarks()
        const { selection } = view.state
        const selectedText = view.state.doc.textBetween(selection.from, selection.to, ' ').trim()
        if (selection.empty || !selectedText) {
          clearSelectionToolbar()
          return
        }
        const from = view.coordsAtPos(selection.from)
        const to = view.coordsAtPos(selection.to)
        setSelectionRect({ left: Math.min(from.left, to.left), right: Math.max(from.right, to.right), top: Math.min(from.top, to.top), bottom: Math.max(from.bottom, to.bottom) })
      })
    }

    document.addEventListener('selectionchange', updateSelectionToolbar)
    document.addEventListener('scroll', updateSelectionToolbar, true)
    document.addEventListener('click', handleDocumentClick)
    window.addEventListener('resize', updateSelectionToolbar)
    editorRef.current?.addEventListener('keyup', updateSelectionToolbar)
    editorRef.current?.addEventListener('mouseup', updateSelectionToolbar)
    return () => {
      cancelAnimationFrame(frame)
      document.removeEventListener('selectionchange', updateSelectionToolbar)
      document.removeEventListener('scroll', updateSelectionToolbar, true)
      document.removeEventListener('click', handleDocumentClick)
      window.removeEventListener('resize', updateSelectionToolbar)
      editorRef.current?.removeEventListener('keyup', updateSelectionToolbar)
      editorRef.current?.removeEventListener('mouseup', updateSelectionToolbar)
    }
  }, [toolbarMode])

  useLayoutEffect(() => {
    if (toolbarMode !== 'selection' || !selectionRect || !toolbarRef.current || !editorRef.current) return
    const reference: VirtualElement = {
      getBoundingClientRect: () => new DOMRect(selectionRect.left, selectionRect.top, Math.max(1, selectionRect.right - selectionRect.left), Math.max(1, selectionRect.bottom - selectionRect.top)),
      contextElement: editorRef.current,
    }
    let active = true
    const updatePosition = () => {
      const toolbar = toolbarRef.current
      if (!toolbar) return
      void computePosition(reference, toolbar, {
        strategy: 'fixed',
        placement: 'top',
        middleware: [offset(8), flip({ padding: 8 }), shift({ padding: 8 })],
      }).then(({ x, y }) => {
        if (active) setToolbarStyle({ left: x, top: y })
      })
    }
    updatePosition()
    const cleanup = autoUpdate(reference, toolbarRef.current, updatePosition)
    return () => {
      active = false
      cleanup()
    }
  }, [selectionRect, toolbarMode, formatOpen, listOpen])

  const run = (command: Command) => {
    const view = viewRef.current
    if (!view || disabled) return
    command(view.state, view.dispatch, view)
    view.focus()
    syncActiveMarks()
    requestAnimationFrame(syncActiveMarks)
    setFormatOpen(false)
    setListOpen(false)
  }

  const setHeading = (level?: number) => run(level ? setBlockType(markdownSchema.nodes.heading, { level }) : setBlockType(markdownSchema.nodes.paragraph))
  const buttonMouseDown = (event: MouseEvent<HTMLButtonElement>) => event.preventDefault()

  const toolbar = toolbarMode !== 'none' && (toolbarMode === 'always' || selectionRect) ? <div ref={toolbarRef} className={`markdown-editor-toolbar ${toolbarMode === 'selection' ? 'markdown-editor-selection-toolbar' : ''}`} style={toolbarMode === 'selection' ? (toolbarStyle ? { left: toolbarStyle.left, top: toolbarStyle.top } : { visibility: 'hidden' }) : undefined} role="toolbar" aria-label="文本格式">
    <button type="button" disabled={disabled} className={activeMarks.strong ? 'is-active' : undefined} onMouseDown={buttonMouseDown} onClick={() => run(toggleMark(markdownSchema.marks.strong))} aria-label="粗体" aria-pressed={activeMarks.strong} title="粗体"><b>B</b></button>
    <button type="button" disabled={disabled} className={activeMarks.em ? 'is-active' : undefined} onMouseDown={buttonMouseDown} onClick={() => run(toggleMark(markdownSchema.marks.em))} aria-label="斜体" aria-pressed={activeMarks.em} title="斜体"><i>I</i></button>
    <div className="markdown-editor-format-wrap">
      <button type="button" disabled={disabled} className="markdown-editor-format-trigger" aria-expanded={formatOpen} onMouseDown={buttonMouseDown} onClick={() => { setFormatOpen(open => !open); setListOpen(false) }}>格式 <i className="ri-arrow-down-s-line" aria-hidden="true" /></button>
      {formatOpen && <div className="markdown-editor-format-menu">
        <button type="button" onMouseDown={buttonMouseDown} onClick={() => setHeading()}>正文</button>
        <button type="button" onMouseDown={buttonMouseDown} onClick={() => setHeading(1)}>标题 1</button>
        <button type="button" onMouseDown={buttonMouseDown} onClick={() => setHeading(2)}>标题 2</button>
        <button type="button" onMouseDown={buttonMouseDown} onClick={() => setHeading(3)}>标题 3</button>
      </div>}
    </div>
    <div className="markdown-editor-list-wrap">
      <button type="button" disabled={disabled} className="markdown-editor-list-trigger" aria-expanded={listOpen} onMouseDown={buttonMouseDown} onClick={() => { setListOpen(open => !open); setFormatOpen(false) }}>列表 <i className="ri-arrow-down-s-line" aria-hidden="true" /></button>
      {listOpen && <div className="markdown-editor-list-menu">
        <button type="button" aria-label="编号列表" title="编号列表" onMouseDown={buttonMouseDown} onClick={() => run(wrapInList(markdownSchema.nodes.ordered_list))}><i className="ri-list-ordered" aria-hidden="true" /></button>
        <button type="button" aria-label="项目符号列表" title="项目符号列表" onMouseDown={buttonMouseDown} onClick={() => run(wrapInList(markdownSchema.nodes.bullet_list))}><i className="ri-list-unordered" aria-hidden="true" /></button>
      </div>}
    </div>
  </div> : null

  const renderedToolbar = toolbarMode === 'selection' && toolbar ? createPortal(toolbar, document.body) : toolbar

  return <div ref={containerRef} className={`markdown-editor ${className}`.trim()} data-toolbar-mode={toolbarMode}>
    {renderedToolbar}
    <div ref={editorRef} className="markdown-editor-surface" {...({ placeholder } as { placeholder: string })} data-placeholder={placeholder} aria-placeholder={placeholder} aria-label={ariaLabel} role="textbox" aria-multiline="true" onKeyDown={onKeyDown} onClick={() => { setFormatOpen(false); setListOpen(false) }} />
  </div>
})
